import { useState } from "react";
import { Group, PersonAdd, MoreVert, Check, PersonAddOutlined } from "@mui/icons-material";
import { Button } from "@/components/ui/Button";

interface CollaboratorData {
  id: string;
  name: string;
  email: string;
  role: 'owner' | 'editor' | 'reviewer' | 'client';
  avatar?: string;
  status: 'active' | 'pending' | 'declined';
  lastActive?: string;
}

const mockCollaborators: CollaboratorData[] = [
  {
    id: "1",
    name: "Sarah Chen",
    email: "sarah@company.com",
    role: "owner",
    status: "active",
    lastActive: "Now",
  },
  {
    id: "2",
    name: "Mike Johnson", 
    email: "mike@client.com",
    role: "client",
    status: "active",
    lastActive: "5 min ago",
  },
  {
    id: "3",
    name: "Alex Rivera",
    email: "alex@law.com",
    role: "reviewer",
    status: "pending",
  },
  {
    id: "4",
    name: "Lisa Wong",
    email: "lisa@company.com", 
    role: "editor",
    status: "active",
    lastActive: "1 hour ago",
  },
];

const roleLabels = {
  owner: "Owner",
  editor: "Editor", 
  reviewer: "Reviewer",
  client: "Client",
};

const roleColors = {
  owner: "bg-purple-500/20 text-purple-300",
  editor: "bg-blue-500/20 text-blue-300",
  reviewer: "bg-green-500/20 text-green-300", 
  client: "bg-orange-500/20 text-orange-300",
};

interface CollaboratorsProps {
  onAddCollaborator?: () => void;
}

export const Collaborators = ({ onAddCollaborator }: CollaboratorsProps) => {
  const [collaborators] = useState<CollaboratorData[]>(mockCollaborators);

  const getStatusIndicator = (status: CollaboratorData['status']) => {
    switch (status) {
      case 'active':
        return <div className="w-3 h-3 bg-green-500 rounded-full" />;
      case 'pending':
        return <div className="w-3 h-3 bg-yellow-500 rounded-full" />;
      case 'declined':
        return <div className="w-3 h-3 bg-red-500 rounded-full" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PersonAddOutlined className="w-5 h-5 text-white" />
          <h3 className="text-lg font-medium">Collaborators</h3>
          <span className="text-xs bg-white/10 text-white px-2 py-1 rounded-full">
            {collaborators.length}
          </span>
        </div>
        <Button 
          onClick={onAddCollaborator}
          size="sm"
          variant="ghost"
          className="p-2"
        >
          <PersonAdd className="w-4 h-4" />
        </Button>
      </div>

      {/* Collaborators list */}
      <div className="space-y-0 max-h-[240px] overflow-y-auto custom-scrollbar">
        {collaborators.map((collaborator) => (
          <div key={collaborator.id} className="flex items-center gap-3 p-3 rounded-2xl hover:bg-white/10 transition-colors">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-b from-neutral-500 to-neutral-600 rounded-full flex items-center justify-center">
                <span className="text-base font-medium text-white">
                  {collaborator.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="absolute -bottom-0 -right-0">
                {getStatusIndicator(collaborator.status)}
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base font-medium text-white truncate">
                  {collaborator.name}
                </span>
                <span className={`text-sm px-2 py-1 rounded-full ${roleColors[collaborator.role]}`}>
                  {roleLabels[collaborator.role]}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <span className="truncate">{collaborator.email}</span>
                {collaborator.status === 'pending' && (
                  <>
                    <span>•</span>
                    <span className="text-yellow-400">Pending</span>
                  </>
                )}
              </div>
            </div>
            
            <button className="text-neutral-400 hover:text-white p-1">
              <MoreVert className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
